/**
 * Outlet034 color-variant detection — shared logic.
 *
 * In this store the "same" product is registered once per color, with the color
 * written into the product title (e.g. "Shorts Beach John John Glam Preto").
 * Colors can be compound ("azul jeans") or words not in any list ("fendi"), so we
 * DON'T rely on knowing every color name. Instead we find the shared MODEL by the
 * common leading-word prefix across the search results, and treat whatever comes
 * after the model (before the brand) as the color.
 *
 * Portado 1:1 da zigma-store (Horizon 3.4.0). Só BRAND_WORDS muda: depende de
 * como o lojista nomeia os produtos. Verificar contra o catálogo real da 034.
 *
 * Consumers: product-combo-builder (assets/outlet034-kit-builder.js). Exposed as window.Outlet034Colors.
 * Search requests have a timeout and are cached per query; 0 results → empty group.
 */
(function () {
  'use strict';

  if (window.Outlet034Colors) return;

  /* Known single-word colors — used only to strip noise before prefix matching. */
  var COLOR_WORDS = [
    'preto', 'preta', 'pretos', 'pretas', 'black',
    'branco', 'branca', 'brancos', 'brancas', 'white',
    'marrom', 'marrons', 'brown',
    'bege', 'beges',
    'azul', 'blue', 'navy',
    'verde', 'green',
    'vermelho', 'vermelha', 'red',
    'rosa', 'pink',
    'cinza', 'cinzas', 'grey', 'gray', 'grafite',
    'laranja', 'orange',
    'amarelo', 'amarela', 'yellow',
    'vinho', 'burgundy', 'bordo',
    'dourado', 'gold',
    'prata', 'silver',
    'roxo', 'roxa', 'purple',
    'caramelo', 'capuccino', 'cappuccino',
    'natural', 'nude', 'caqui', 'khaki', 'areia', 'sand',
    'creme', 'cream', 'terracota', 'coral', 'mostarda', 'indigo',
    'petroleo', 'oceano', 'lavanda', 'menta', 'mint', 'oliva', 'olive',
    'salmao', 'salmon', 'tijolo', 'ferrugem', 'rust', 'taupe', 'cobre', 'copper',
    'fendi', 'jeans', 'marinho', 'militar'
  ];

  var COLOR_SET = {};
  COLOR_WORDS.forEach(function (c) { COLOR_SET[c] = true; });

  /* Brand words that may trail the title. */
  var BRAND_WORDS = {
    outlet034: true, diesel: true, lacoste: true, reserva: true, oficina: true,
    ellus: true, forum: true, urban: true, acostamento: true, abercrombie: true,
    versatilidad: true,
  };

  function lowerWords(title) {
    return (title || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip accents
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  }

  /* Drop a trailing brand word (known brand, or any short non-color last word). */
  function stripBrand(words) {
    if (!words.length) return words;
    var last = words[words.length - 1];
    if (BRAND_WORDS[last] || (last.length <= 10 && !COLOR_SET[last])) {
      return words.slice(0, -1);
    }
    return words.slice();
  }

  function removeColors(words) {
    return words.filter(function (w) { return !COLOR_SET[w]; });
  }

  function commonPrefixLen(a, b) {
    var n = Math.min(a.length, b.length);
    var i = 0;
    while (i < n && a[i] === b[i]) i++;
    return i;
  }

  function capitalize(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }

  /* Legacy helper kept for the cache key and the combo builder. */
  function getBaseName(title) {
    return removeColors(stripBrand(lowerWords(title))).join(' ').trim();
  }

  /* Best-effort single-product color (used by the combo builder for the current step). */
  function extractColor(title, baseName) {
    var brandless = stripBrand(lowerWords(title));
    var modelWords = removeColors(brandless);
    // Whatever in the brandless title isn't part of the (color-stripped) model is the color.
    var color = brandless.filter(function (w) { return COLOR_SET[w]; }).join(' ');
    if (!color) {
      // fall back to words after the model length
      color = brandless.slice(modelWords.length).join(' ');
    }
    return capitalize(color);
  }

  /* /search/suggest with timeout + in-memory cache (kit steps often repeat the same product). */
  var SEARCH_TIMEOUT = 4000;
  var searchCache = {};

  function searchProducts(query) {
    if (searchCache[query]) return searchCache[query];
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, SEARCH_TIMEOUT) : null;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last';
    var p = fetch(url, ctrl ? { signal: ctrl.signal, headers: { Accept: 'application/json' } } : undefined)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        var list = data && data.resources && data.resources.results && data.resources.results.products;
        return Array.isArray(list) ? list : [];
      })
      .catch(function (err) {
        delete searchCache[query];
        if (!err || err.name !== 'AbortError') console.warn('Outlet034Colors search error:', err);
        return [];
      })
      .finally(function () { if (timer) clearTimeout(timer); });
    searchCache[query] = p;
    return p;
  }

  /**
   * Find the color group sharing the same model as the given product.
   * @returns {Promise<{modelLen:number, members:Array<{color,handle,url,image,isCurrent}>}>}
   */
  async function getColorGroup(title, handle, currentImage) {
    var empty = { modelLen: 0, members: [] };
    if (!title || !handle) return empty;

    var curBrandless = stripBrand(lowerWords(title));
    var curStripped = removeColors(curBrandless);
    if (curStripped.length < 2) return empty;

    /*
     * Build a broad-but-specific search query. We DROP the last word, because it
     * is usually a residual/unknown color word ("sage", "jeans") that isn't in
     * COLOR_WORDS. Keeping it would narrow /search/suggest (which AND-matches
     * terms) down to the current product only, returning no siblings. The
     * prefix matching below restores precision.
     */
    var queryWords = curStripped.length > 2 ? curStripped.slice(0, -1) : curStripped;
    var query = queryWords.join(' ').trim();
    if (query.length < 4) return empty;

    var products = await searchProducts(query);
    if (!products.length) return empty;

    var cands = [];
    products.forEach(function (p) {
      if (!p.handle || p.handle === handle) return;
      var w = stripBrand(lowerWords(p.title));
      var sw = removeColors(w);
      var cpl = commonPrefixLen(curStripped, sw);
      if (cpl >= 2) {
        cands.push({ handle: p.handle, image: p.image || (p.featured_image && (p.featured_image.url || p.featured_image)) || '', words: w, cpl: cpl });
      }
    });
    if (!cands.length) return empty;

    // The model = the deepest leading-word prefix shared with a sibling.
    var M = 0;
    cands.forEach(function (c) { if (c.cpl > M) M = c.cpl; });
    if (M < 2) return empty;
    // Current product must overlap the model strongly (guards against unrelated groups).
    if (curStripped.length - M > 2) return empty;

    function colorOf(words) {
      return capitalize(words.slice(M).join(' ').trim());
    }

    var members = [
      {
        color: colorOf(curBrandless) || 'Original',
        handle: handle,
        url: '/products/' + handle,
        image: currentImage || '',
        isCurrent: true,
      },
    ];

    cands.forEach(function (c) {
      if (c.cpl !== M) return;
      if (c.words.length - M > 2) return; // sibling color should be 1-2 words
      members.push({
        color: colorOf(c.words) || 'Cor',
        handle: c.handle,
        url: '/products/' + c.handle,
        image: c.image,
        isCurrent: false,
      });
    });

    return { modelLen: M, members: members };
  }

  /* Siblings only (excludes current). Kept for the combo builder. */
  async function findSiblings(title, handle) {
    var group = await getColorGroup(title, handle, '');
    return group.members
      .filter(function (m) { return !m.isCurrent; })
      .map(function (m) {
        return { color: m.color, handle: m.handle, url: m.url, image: m.image };
      });
  }

  window.Outlet034Colors = {
    COLOR_WORDS: COLOR_WORDS,
    getBaseName: getBaseName,
    extractColor: extractColor,
    getColorGroup: getColorGroup,
    findSiblings: findSiblings,
  };
})();
